import { Checkbox } from '@thanx/uikit/checkbox'
import { Text } from '@thanx/uikit/text'
import type { OrderHandoff, ServiceCharge } from 'api/serviceCharges'
import { isRecommendedForDelivery } from 'api/serviceCharges'
import { FocusOnce } from 'baseui/modal'
import ModalContainer from 'components/ModalContainer'
import PopoverContent from 'components/PopoverContent'
import { buildTranslate } from 'locales'
import React from 'react'
import { formatServiceChargeAmount } from '../../serviceChargeFormat'
import Pill from './Pill'

const t = buildTranslate(
  'locations.location_list.location_detail.service_charges'
)

type Props = {
  charge: ServiceCharge | null
  draftHandoffs: OrderHandoff[]
  onToggleHandoff: (handoff: OrderHandoff, next: boolean) => void
  onCancel: () => void
  onSave: () => void
  isSaving: boolean
}

const infoButtonAriaLabel = (label: string): string =>
  t('table.info_button_aria', { label }) as string

const metaLine = (charge: ServiceCharge): string =>
  [
    t('edit_modal.meta_id', { id: charge.provider_uid }) as string,
    charge.gratuity ? (t('gratuity_tag') as string) : null,
    `${t(
      `amount_type_labels.${charge.amount_type}`
    )} ${formatServiceChargeAmount(charge)}`,
  ]
    .filter(Boolean)
    .join(' · ')

const ServiceChargeEditModal = ({
  charge,
  draftHandoffs,
  onToggleHandoff,
  onCancel,
  onSave,
  isSaving,
}: Props) => {
  return (
    <ModalContainer
      isOpen={charge !== null}
      onClose={onCancel}
      onProceed={onSave}
      title={
        charge ? (t('edit_modal.title', { name: charge.name }) as string) : ''
      }
      proceedText={t('edit_modal.save') as string}
      isLoading={isSaving}
      width="440px"
    >
      {charge && (
        <>
          <FocusOnce>
            <div tabIndex={-1}>
              <Text.BodyText4 tag="p" color="grey70" className="mt-s mb-m">
                {t('edit_modal.description')}
              </Text.BodyText4>
            </div>
          </FocusOnce>
          <div className="d-flex align-items-center flex-wrap mb-xxs">
            <Text.BodyText4 bold tag="p" color="grey90" className="mr-xs mb-0">
              {charge.name}
            </Text.BodyText4>
            {isRecommendedForDelivery(charge) && (
              <Pill tone="positive">{t('recommended_badge')}</Pill>
            )}
          </div>
          <Text.BodyText5 tag="p" color="grey50" className="mb-m">
            {metaLine(charge)}
          </Text.BodyText5>
          <Text.BodyText4 bold tag="p" color="grey90" className="mb-s">
            {t('edit_modal.question')}
          </Text.BodyText4>
          <div className="d-flex align-items-center mb-s">
            <Checkbox
              checked={draftHandoffs.includes('DELIVERY')}
              disabled={isSaving}
              ariaLabel={
                t('delivery_toggle_aria', { name: charge.name }) as string
              }
              onChange={(next: boolean) => onToggleHandoff('DELIVERY', next)}
            >
              {t('table.delivery')}
            </Checkbox>
            <PopoverContent
              className="ml-xs"
              ariaLabel={infoButtonAriaLabel(t('table.delivery') as string)}
              sections={[{ content: t('table.delivery_help') as string }]}
            />
          </div>
          <div className="d-flex align-items-center mb-s">
            <Checkbox
              checked={draftHandoffs.includes('PICKUP')}
              disabled={isSaving}
              ariaLabel={
                t('pickup_toggle_aria', { name: charge.name }) as string
              }
              onChange={(next: boolean) => onToggleHandoff('PICKUP', next)}
            >
              {t('table.pickup_takeout')}
            </Checkbox>
            <PopoverContent
              className="ml-xs"
              ariaLabel={infoButtonAriaLabel(
                t('table.pickup_takeout') as string
              )}
              sections={[{ content: t('table.pickup_takeout_help') as string }]}
            />
          </div>
        </>
      )}
    </ModalContainer>
  )
}

export default ServiceChargeEditModal
